import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CpuChipIcon,
  FireIcon,
  CloudIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  SparklesIcon,
  CogIcon,
  TruckIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  ArrowRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  CubeIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';

const TechSolutions = () => {
  const { t } = useTranslation();
  const [activeSolution, setActiveSolution] = useState('refrigeratorMonitoring');

  const solutions = [
    {
      id: 'refrigeratorMonitoring',
      name: t('techSolutions.refrigeratorMonitoring.title'),
      icon: CubeIcon,
      color: 'from-green-500 to-green-600',
      description: t('techSolutions.refrigeratorMonitoring.description'),
      features: [
        t('techSolutions.refrigeratorMonitoring.feature1'),
        t('techSolutions.refrigeratorMonitoring.feature2'),
        t('techSolutions.refrigeratorMonitoring.feature3'),
        t('techSolutions.refrigeratorMonitoring.feature4')
      ]
    },
    {
      id: 'freezerMonitoring',
      name: t('techSolutions.freezerMonitoring.title'),
      icon: FireIcon,
      color: 'from-blue-500 to-blue-600',
      description: t('techSolutions.freezerMonitoring.description'),
      features: [
        t('techSolutions.freezerMonitoring.feature1'),
        t('techSolutions.freezerMonitoring.feature2'),
        t('techSolutions.freezerMonitoring.feature3'),
        t('techSolutions.freezerMonitoring.feature4')
      ]
    },
    {
      id: 'ln2Monitoring',
      name: t('techSolutions.ln2Monitoring.title'),
      icon: BeakerIcon,
      color: 'from-purple-500 to-purple-600',
      description: t('techSolutions.ln2Monitoring.description'),
      features: [
        t('techSolutions.ln2Monitoring.feature1'),
        t('techSolutions.ln2Monitoring.feature2'),
        t('techSolutions.ln2Monitoring.feature3'),
        t('techSolutions.ln2Monitoring.feature4')
      ]
    },
    {
      id: 'senseAnywhere',
      name: t('techSolutions.senseAnywhere.title'),
      icon: CloudIcon,
      color: 'from-orange-500 to-orange-600',
      description: t('techSolutions.senseAnywhere.description'),
      features: [
        t('techSolutions.senseAnywhere.feature1'),
        t('techSolutions.senseAnywhere.feature2'),
        t('techSolutions.senseAnywhere.feature3'),
        t('techSolutions.senseAnywhere.feature4')
      ]
    },
    {
      id: 'sensMed',
      name: t('techSolutions.sensMed.title'),
      icon: TruckIcon,
      color: 'from-teal-500 to-teal-600',
      description: t('techSolutions.sensMed.description'),
      features: [
        t('techSolutions.sensMed.feature1'),
        t('techSolutions.sensMed.feature2'),
        t('techSolutions.sensMed.feature3'),
        t('techSolutions.sensMed.feature4')
      ]
    },
    {
      id: 'sampleLynx',
      name: t('techSolutions.sampleLynx.title'),
      icon: ChartBarIcon,
      color: 'from-indigo-500 to-indigo-600',
      description: t('techSolutions.sampleLynx.description'),
      features: [
        t('techSolutions.sampleLynx.feature1'),
        t('techSolutions.sampleLynx.feature2'),
        t('techSolutions.sampleLynx.feature3'),
        t('techSolutions.sampleLynx.feature4')
      ]
    },
    {
      id: 'maintenance',
      name: t('techSolutions.maintenance.title'),
      icon: WrenchScrewdriverIcon,
      color: 'from-red-500 to-red-600',
      description: t('techSolutions.maintenance.description'),
      features: [
        t('techSolutions.maintenance.feature1'),
        t('techSolutions.maintenance.feature2'),
        t('techSolutions.maintenance.feature3'),
        t('techSolutions.maintenance.feature4')
      ]
    }
  ];

  const benefits = [
    {
      icon: ShieldCheckIcon,
      title: t('techSolutions.benefits.compliance.title'),
      description: t('techSolutions.benefits.compliance.description'),
      color: 'from-green-500 to-green-600'
    },
    {
      icon: ClockIcon,
      title: t('techSolutions.benefits.realTime.title'),
      description: t('techSolutions.benefits.realTime.description'),
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: CogIcon,
      title: t('techSolutions.benefits.integration.title'),
      description: t('techSolutions.benefits.integration.description'),
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: PhoneIcon,
      title: t('techSolutions.benefits.support.title'),
      description: t('techSolutions.benefits.support.description'),
      color: 'from-orange-500 to-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-green via-brand-blue to-brand-blueSecondary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-full backdrop-blur-sm">
                <CpuChipIcon className="w-16 h-16" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t('techSolutions.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              {t('techSolutions.hero.subtitle')}
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 inline-block">
              <div className="flex items-center justify-center gap-3 text-lg">
                <EnvelopeIcon className="w-6 h-6" />
                <span>{t('techSolutions.hero.contactInfo')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('techSolutions.solutions.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('techSolutions.solutions.subtitle')}
            </p>
          </motion.div>

          {/* Solution Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {solutions.map((solution) => (
              <button
                key={solution.id}
                onClick={() => setActiveSolution(solution.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all text-sm lg:text-base ${
                  activeSolution === solution.id
                    ? `bg-gradient-to-r ${solution.color} text-white shadow-lg`
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <solution.icon className="w-5 h-5" />
                <span className="hidden lg:inline">{solution.name}</span>
                <span className="lg:hidden">{solution.name.split(' ')[0]}</span>
              </button>
            ))}
          </div>

          {/* Active Solution Details */}
          {solutions.map((solution) => (
            activeSolution === solution.id && (
              <motion.div
                key={solution.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-3 bg-gradient-to-r ${solution.color} rounded-full`}>
                    <solution.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {solution.name}
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  {solution.description}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start text-gray-700">
                      <CheckCircleIcon className="w-6 h-6 text-green-500 ml-3 flex-shrink-0 mt-0.5" />
                      <span className="text-sm lg:text-base">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('techSolutions.benefits.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('techSolutions.benefits.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className={`p-4 bg-gradient-to-r ${benefit.color} rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center`}>
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('techSolutions.howItWorks.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('techSolutions.howItWorks.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-8 items-center">
            {[1, 2, 3, 4, 5].map((step, index) => (
              <React.Fragment key={step}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-brand-green to-brand-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                    {step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {t(`techSolutions.howItWorks.step${step}.title`)}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t(`techSolutions.howItWorks.step${step}.description`)}
                  </p>
                </motion.div>
                {index < 4 && (
                  <div className="hidden md:flex justify-center">
                    <ArrowRightIcon className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('techSolutions.industries.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('techSolutions.industries.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((industry, index) => (
              <motion.div
                key={industry}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl text-center hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-bold text-gray-900 text-sm lg:text-base">
                  {t(`techSolutions.industries.industry${industry}`)}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-green to-brand-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <SparklesIcon className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">
              {t('techSolutions.cta.title')}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t('techSolutions.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                <EnvelopeIcon className="w-5 h-5" />
                <span className="font-medium">{t('techSolutions.cta.contactEmail')}</span>
              </div>
              <a
                href={`mailto:${t('techSolutions.cta.contactEmail')}`}
                className="bg-white text-brand-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                {t('techSolutions.cta.contactButton')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TechSolutions;
